/**
 * Processing Finalizer - Universal 3-step wrap-up for all processing phases
 *
 * This module handles the universal 3-step sequence that must happen after ANY
 * processing changes (first-fit, best-fit, optimization, manual adjustments):
 *
 * 1. Complete cash flows - Update DRF balances based on new numbers
 * 2. Build tax year plan - Create annual financial view
 * 3. Evaluate results - Calculate viability and metrics
 *
 * Previously duplicated across CandidateProcessor and utilization-optimizer.
 */

import { TaxYearPlanBuilder } from '~/builders/tax-year-plan/TaxYearPlanBuilder';
import { CashFlowCalculator } from '~/engine/processing/engines/cashflow-engine/drf-calculator';
import { ResultsEvaluator } from '~/engine/processing/engines/cashflow-engine/results-evaluator';
import { TargetComplianceChecker } from '~/engine/processing/engines/cashflow-engine/target-compliance';
import { validateFiveYearRuleCompliance } from '~/engine/processing/managers/optimizers/five-year-rule-optimizer';
// ProcessingContext removed; logging and phase flow handled via options and candidate
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';

export interface FinalizationOptions {
  skipTaxYearPlan?: boolean; // For cases where tax year plan already generated
  skipResultsEvaluation?: boolean; // For analysis-only scenarios
  skipFiveYearRuleValidation?: boolean; // For first-fit/best-fit phases where violations are acceptable
  phase?: string; // For logging context
  configuration: Configuration; // Required: Configuration data for DRF inputs and target compliance checks
}

export interface FinalizationResult {
  success: boolean;
  processingTime: number;
  phase: string;
  stepsCompleted: Array<'cashflows' | 'taxyearplan' | 'results'>;
  error?: string;
}

export class ProcessingFinalizer {
  /**
   * Executes the universal 3-step wrap-up sequence
   * This should be called after ANY changes to candidate cash flows or financial data
   */
  static finalize(candidate: Candidate, options: FinalizationOptions): FinalizationResult {
    const startTime = Date.now();
    const {
      skipTaxYearPlan = false,
      skipResultsEvaluation = false,
      phase = 'PROCESSING',
      configuration,
    } = options;

    // Validate configuration is provided (required for DRF calculations)
    if (!configuration) {
      throw new Error(
        'Configuration is required for finalization. ' +
          'DRF inputs are accessed via configuration.drfInputs.'
      );
    }

    const stepsCompleted: Array<'cashflows' | 'taxyearplan' | 'results'> = [];

    logger.L4.debug(`🔄 ${phase}: Finalizing candidate ${candidate.name}`);

    try {
      // Step 1: Complete cash flows - Update DRF balances based on new numbers
      // This MUST happen first as it recalculates all DRF balances sequentially
      // Note: DRF information is now accessed via configuration.drfInputs
      CashFlowCalculator.calculate(candidate, configuration);
      stepsCompleted.push('cashflows');

      logger.L4.trace(`✅ ${phase}: Cash flows updated for ${candidate.name}`);

      // Step 2: Build tax year plan - Create annual financial view
      // This provides the annual summary view needed for user communication
      if (!skipTaxYearPlan) {
        try {
          const validation = TaxYearPlanBuilder.validateCandidateForPlanGeneration(candidate);
          if (validation.isValid) {
            TaxYearPlanBuilder.buildTaxYearPlan(candidate);
            stepsCompleted.push('taxyearplan');

            logger.L4.trace(`✅ ${phase}: Tax year plan generated for ${candidate.name}`);
          } else {
            logger.L4.warn(
              `⚠️ ${phase}: Cannot generate tax year plan for ${candidate.name}: ${validation.errors}`
            );
            // Don't fail entire finalization - tax year plan is supplementary
          }
        } catch (error) {
          logger.error(
            `❌ ${phase}: Error generating tax year plan for ${candidate.name}`,
            error as Error
          );
          // Don't fail entire finalization - tax year plan generation shouldn't break processing
          candidate.taxYearBasedProjectPlan = [];
        }
      }

      // Step 3: Evaluate results - Calculate viability and metrics
      // This MUST happen last as it depends on accurate cash flows and may use tax year data
      if (!skipResultsEvaluation) {
        logger.domain.compliance(`📊 ${phase}: About to evaluate results for ${candidate.name}`);
        ResultsEvaluator.updateCandidateResults(candidate, configuration);

        // Step 3a: Target compliance check - Validate targeted bond constraints
        // This happens after results evaluation but before five-year rule
        // Only check if candidate is still viable after DRF checks
        // Target compliance check (requires configuration)
        if (configuration) {
          const hasTargets = TargetComplianceChecker.hasTargetedBonds(candidate, configuration);

          // Debug: Check what bonds we have and their targets
          const bondInfo = configuration.bondSeries
            .map(
              (b) =>
                `${b.name}: target=${b.targetInformation ? JSON.stringify(b.targetInformation) : 'none'}`
            )
            .join(', ');

          logger.domain.compliance(
            `🔍 ${phase}: Candidate ${candidate.name} - Viable: ${candidate.results.isViable}, Has Targets: ${hasTargets}, Bond Count: ${configuration.bondSeries.length}`
          );
          logger.domain.compliance(`   Bonds: ${bondInfo}`);

          if (candidate.results.isViable && hasTargets) {
            logger.domain.compliance(
              `🎯 ${phase}: Checking target compliance for ${candidate.name}`
            );
            TargetComplianceChecker.checkTargetCompliance(candidate, configuration);
          }
        } else {
          logger.L4.debug(
            `⚠️ ${phase}: Skipping target compliance check for ${candidate.name} - configuration not provided`
          );
        }

        // Step 3b: Five-year rule validation - Final regulatory compliance check
        // Only run during optimization phase or when explicitly requested
        // Skip for first-fit/best-fit where violations are acceptable and trigger optimization
        if (!options.skipFiveYearRuleValidation) {
          logger.L4.trace(`🔍 ${phase}: Running five-year rule validation for ${candidate.name}`);
          if (configuration) {
            validateFiveYearRuleCompliance(candidate, configuration);
          }
        } else {
          logger.L4.trace(`⏭️ ${phase}: Skipping five-year rule validation for ${candidate.name}`);
        }

        stepsCompleted.push('results');

        const status = candidate.results.isViable ? '✅ VIABLE' : '❌ NOT VIABLE';
        logger.L4.debug(`✅ ${phase}: Results evaluated for ${candidate.name} - ${status}`);
      }

      const processingTime = Date.now() - startTime;

      logger.L4.info(
        `🏁 ${phase}: Finalization complete for ${candidate.name} in ${processingTime}ms`
      );

      return {
        success: true,
        processingTime,
        phase,
        stepsCompleted,
      };
    } catch (error) {
      const processingTime = Date.now() - startTime;
      logger.error(`❌ ${phase}: Finalization failed for ${candidate.name}`, error as Error);

      return {
        success: false,
        processingTime,
        phase,
        stepsCompleted,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Finalizes multiple candidates using the same options
   * Useful for batch finalization after bulk processing changes
   */
  static finalizeMultiple(
    candidates: Candidate[],
    options: FinalizationOptions
  ): FinalizationResult[] {
    return candidates.map((candidate) => ProcessingFinalizer.finalize(candidate, options));
  }

  /**
   * Quick finalization for optimization scenarios
   * Uses standard settings optimized for post-optimization processing
   * Note: Configuration is required for DRF calculations
   */
  static finalizeAfterOptimization(
    candidate: Candidate,
    configuration: Configuration
  ): FinalizationResult {
    return ProcessingFinalizer.finalize(candidate, {
      phase: 'OPTIMIZATION',
      configuration,
    });
  }

  /**
   * Quick finalization for standard processing scenarios
   * Uses standard settings optimized for first-fit/best-fit processing
   * Skips five-year rule validation since violations are acceptable and trigger optimization
   */
  static finalizeAfterProcessing(
    candidate: Candidate,
    configuration: Configuration,
    options: Partial<Omit<FinalizationOptions, 'configuration'>> = {}
  ): FinalizationResult {
    // Merge configuration into options
    const finalOptions: FinalizationOptions = { ...options, configuration };
    return ProcessingFinalizer.finalize(candidate, finalOptions);
  }
}
